export const SHOPPING_REGION_STORAGE_KEY = "housemind_shopping_region";
export const LEGACY_SHOPPING_REGION_STORAGE_KEY = "hm_shopping_region";

export const SHOPPING_REGION_OPTIONS = [
  { value: "US", label: "United States" },
  { value: "UK", label: "United Kingdom" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  { value: "NG", label: "Nigeria" },
  { value: "ZA", label: "South Africa" },
  { value: "KE", label: "Kenya" },
  { value: "GH", label: "Ghana" },
  { value: "INTL", label: "International / other" },
];

const AMAZON_DOMAINS = {
  US: "www.amazon.com",
  UK: "www.amazon.co.uk",
  CA: "www.amazon.ca",
  AU: "www.amazon.com.au",
};

function normalizeRegion(region) {
  return String(region || "US").trim().toUpperCase();
}

export function detectShoppingRegion() {
  if (typeof navigator === "undefined") return "US";
  const locale = (navigator.languages?.[0] || navigator.language || "en-US").trim();
  const region = locale.includes("-") ? locale.split("-").pop().toUpperCase() : "";
  const regionMap = {
    GB: "UK",
    UK: "UK",
    US: "US",
    CA: "CA",
    AU: "AU",
    NG: "NG",
    ZA: "ZA",
    KE: "KE",
    GH: "GH",
  };
  return regionMap[region] || "INTL";
}

export function getStoredShoppingRegion() {
  if (typeof window === "undefined") return "US";
  const saved = localStorage.getItem(SHOPPING_REGION_STORAGE_KEY) || localStorage.getItem(LEGACY_SHOPPING_REGION_STORAGE_KEY);
  return normalizeRegion(saved || detectShoppingRegion());
}

export function setStoredShoppingRegion(region) {
  if (typeof window === "undefined") return;
  const next = String(region || "").trim().toUpperCase();
  if (!next) {
    localStorage.removeItem(SHOPPING_REGION_STORAGE_KEY);
    localStorage.removeItem(LEGACY_SHOPPING_REGION_STORAGE_KEY);
    return;
  }
  localStorage.setItem(SHOPPING_REGION_STORAGE_KEY, next);
  localStorage.setItem(LEGACY_SHOPPING_REGION_STORAGE_KEY, next);
}

export function buildAmazonSearchUrl(query, region = "US") {
  if (!query) return null;
  const domain = AMAZON_DOMAINS[normalizeRegion(region)] || AMAZON_DOMAINS.US;
  const params = new URLSearchParams({
    k: query,
    ref: "housemind",
  });
  return `https://${domain}/s?${params.toString()}`;
}

export function buildEtsySearchUrl(query) {
  if (!query) return null;
  return `https://www.etsy.com/search?q=${encodeURIComponent(query)}&ref=housemind`;
}

export function buildWalmartSearchUrl(query, region = "US") {
  if (!query || normalizeRegion(region) !== "US") return null;
  return `https://www.walmart.com/search?q=${encodeURIComponent(query)}`;
}

export function buildMacysSearchUrl(query, region = "US") {
  if (!query || normalizeRegion(region) !== "US") return null;
  return `https://www.macys.com/shop/featured/${encodeURIComponent(query)}`;
}

export function buildUncommonGoodsUrl(query) {
  if (!query) return null;
  return `https://www.uncommongoods.com/search?query=${encodeURIComponent(query)}&utm_source=housemind`;
}

export function buildGiftCardsUrl(query) {
  // GiftCards.com doesn't have a public search URL — link to a relevant category page
  if (!query) return "https://www.giftcards.com/?utm_source=housemind";
  return `https://www.giftcards.com/?utm_source=housemind&q=${encodeURIComponent(query)}`;
}

export function buildRetailerLinks({
  title,
  amazonSearchQuery,
  etsySearchQuery,
  amazonUrl,
  etsyUrl,
  shoppingRegion,
  // contextual flags — set by the gift suggestion page
  isUnique = false,
  isLastMinute = false,
}) {
  const region = normalizeRegion(shoppingRegion);
  const genericQuery = amazonSearchQuery || title;
  const links = [];

  const addLink = (key, label, url, className, program) => {
    if (!url || links.some(link => link.key === key || link.url === url)) return;
    links.push({ key, label, url, className, program: program || key });
  };

  addLink(
    "amazon",
    "Amazon",
    region === "US" ? (amazonUrl || buildAmazonSearchUrl(genericQuery || title, region)) : buildAmazonSearchUrl(genericQuery || title, region),
    "affiliate-amazon",
    "amazon"
  );

  addLink(
    "walmart",
    "Walmart",
    buildWalmartSearchUrl(genericQuery || title, region),
    "affiliate-walmart",
    "walmart"
  );

  addLink(
    "macys",
    "Macy's",
    buildMacysSearchUrl(genericQuery || title, region),
    "affiliate-macys",
    "macys"
  );

  addLink(
    "etsy",
    "Etsy",
    etsyUrl || buildEtsySearchUrl(etsySearchQuery || title),
    "affiliate-etsy",
    "etsy"
  );

  // Contextual: Uncommon Goods for unique / experience gifts
  if (isUnique) {
    addLink(
      "uncommon_goods",
      "Uncommon Goods",
      buildUncommonGoodsUrl(genericQuery || title),
      "affiliate-uncommongoods",
      "uncommon_goods"
    );
  }

  // Contextual: GiftCards.com for last-minute (≤ 3 days)
  if (isLastMinute) {
    addLink(
      "giftcards_com",
      "Gift Cards",
      buildGiftCardsUrl(genericQuery || title),
      "affiliate-giftcards",
      "giftcards_com"
    );
  }

  return links;
}

function getStoreMetaFromUrl(url) {
  if (!url) return null;
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname.includes("amazon.")) {
      return { key: "amazon", label: "Amazon", className: "affiliate-amazon" };
    }
    if (hostname.includes("etsy.")) {
      return { key: "etsy", label: "Etsy", className: "affiliate-etsy" };
    }
    if (hostname.includes("walmart.")) {
      return { key: "walmart", label: "Walmart", className: "affiliate-walmart" };
    }
    if (hostname.includes("macys.")) {
      return { key: "macys", label: "Macy's", className: "affiliate-macys" };
    }
    const hostParts = hostname.replace(/^www\./, "").split(".");
    const siteName = hostParts[0];
    if (!siteName) return null;
    return {
      key: "exact",
      label: siteName.charAt(0).toUpperCase() + siteName.slice(1),
      className: "affiliate-exact",
    };
  } catch {
    return null;
  }
}

export function buildRegistryPurchaseLinks({
  title,
  amazonSearchQuery,
  etsySearchQuery,
  amazonUrl,
  etsyUrl,
  customUrl,
  shoppingRegion,
  isUnique = false,
  isLastMinute = false,
}) {
  const retailerLinks = buildRetailerLinks({
    title,
    amazonSearchQuery,
    etsySearchQuery,
    amazonUrl,
    etsyUrl,
    shoppingRegion,
    isUnique,
    isLastMinute,
  });
  const storeMeta = getStoreMetaFromUrl(customUrl);
  const exactLink = customUrl
    ? {
        key: `exact-${storeMeta?.key || "item"}`,
        label: storeMeta?.label || "Store",
        url: customUrl,
        className: storeMeta?.className || "affiliate-exact",
        isExact: true,
      }
    : null;

  const seenUrls = new Set();
  const dedupedRetailers = retailerLinks.filter((link) => {
    if (!link?.url || link.url === customUrl || seenUrls.has(link.url)) return false;
    seenUrls.add(link.url);
    return true;
  });

  const primaryLink = exactLink || dedupedRetailers[0] || null;
  const secondaryLinks = exactLink ? dedupedRetailers : dedupedRetailers.slice(1);

  return {
    primaryLink: primaryLink
      ? {
          ...primaryLink,
          ctaLabel: primaryLink.isExact ? `View exact item${primaryLink.label ? ` on ${primaryLink.label}` : ""}` : `View on ${primaryLink.label}`,
        }
      : null,
    secondaryLinks,
  };
}
